import * as yup from 'yup';
import { merge } from 'lodash-es';
import { RendererType } from '../data-models/field-definition';
import { NoGroupFundLabel } from '@foresightdata/web-common-core';
import {
  dateField,
  fundTypeField,
  integerField,
  multiplierField,
  percentField,
  percentFieldHighPrecision0to100,
  usdField,
  userField,
} from './common-schema-defs';
import { allocationSettingsSchema } from './FundAllocationSettings.schema';

export enum CalculationType {
  lpOnly = 'lp_only',
  combined = 'combined',
  mixed = 'mixed',
}

export const IncomeType = {
  niiOnly: 'niiOnly',
  netIncomeGp: 'netIncomeGp',
} as const;
export type IncomeType = (typeof IncomeType)[keyof typeof IncomeType];
export const IncomeTypeToLabel: Record<IncomeType, string> = {
  niiOnly: 'NII Only',
  netIncomeGp: 'Net Income (GP)',
};

// Mirrors the backend LpNavCalculationType enum (fund.entity.ts). Values must match the API.
export const LpNavCalculationType = {
  fundWaterfall: 'fund_waterfall',
  ledgerIngestion: 'ledger_ingestion',
  manual: 'manual',
} as const;
export type LpNavCalculationType = (typeof LpNavCalculationType)[keyof typeof LpNavCalculationType];
export const LpNavCalculationTypeToLabel: Record<LpNavCalculationType, string> = {
  fund_waterfall: 'Fund Waterfall',
  ledger_ingestion: 'Ledger Ingestion',
  manual: 'Manual',
};

export function fundFields() {
  return {
    aliases: yup.array(yup.string().required()).nullable().default([]),
    allocatedCommittedCapitalPercentage: percentField().nullable().default(null).label('Total Allocation'),
    allocationSettings: allocationSettingsSchema().nullable().default(null),
    calculationType: yup
      .string()
      .oneOf(Object.values(CalculationType))
      .nullable()
      .default(CalculationType.combined),
    cartaId: yup.string().nullable().default(null).label('Carta ID'),
    committedCapital: usdField().nullable().label('Life of Fund Committed Capital').default(null),
    commitments: usdField().min(0).label('Commitment').nullable().default(null),
    contributedSecurities: yup.number().nullable().default(null),
    createdAt: yup.string().nullable().default(null),
    createdBy: yup.string().nullable().default(null),
    reportingCurrency: yup.string().default('USD').label('Reporting Currency'),
    enableGPCatchup: yup.boolean().nullable().default(false).label('GP Catch-Up'),
    enableSuperReturn: yup.boolean().nullable().default(false).label('Super Return'),
    feesAndExpensesAllocationAmount: usdField().nullable().default(null).label(''),
    feesAndExpensesAllocationPercentage: percentField()
      .nullable()
      .default(null)
      .label('Fees & Expenses Allocation (Life of Fund)'),
    finalCloseDate: dateField().nullable().default(null),
    followOnInvestingWindowCloseDate: dateField()
      .nullable()
      .default(null)
      .label('Fund Investing Window Close (Follow On)'),
    fundType: fundTypeField().default(NoGroupFundLabel),
    fundKind: yup.string().oneOf(['fund', 'intermediary', 'family']).default('fund').label('Fund Kind'),
    familyId: yup.number().nullable().default(null).label('Fund Family'),
    generalPartners: userField().nullable().default([]),
    gpCatchUpPercentage: percentFieldHighPrecision0to100()
      .nullable()
      .label('GP Catch-Up Percentage')
      .default(25),
    gpCatchUpThreshold: yup.number().nullable().default(null),
    id: yup.number().required().default(-1),
    iLevelId: yup.string().nullable().default(''),
    inceptionDate: dateField().nullable().default(null),
    incomeType: yup
      .string()
      .oneOf(Object.values(IncomeType))
      .nullable()
      .default(null)
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: Object.values(IncomeType).map((value) => ({
            displayName: IncomeTypeToLabel[value],
            value,
          })),
        },
      }),
    initialInvestingWindowCloseDate: dateField()
      .nullable()
      .default(null)
      .label('Fund Investing Window Close (Initial Investments)'),
    initialInvestmentAllocationAmount: usdField().nullable().default(null).label(''),
    initialInvestmentAllocationPercentage: percentField()
      .nullable()
      .default(null)
      .label('Initial Investment Allocation (Life of Fund)'),
    initialManagementFee: percentField().nullable().default(null),
    initialPostMoney: usdField().nullable().default(null),
    investedManagementFee: percentField().nullable().default(null),
    investingWindowInceptionDate: dateField()
      .nullable()
      .default(null)
      .label('Fund Investing Window Inception'),
    investmentPeriodEndDate: dateField().nullable().default(null),
    isCommitmentUsedInsteadOfLPContributions: yup
      .boolean()
      .nullable()
      .default(false)
      .label('Use contributions to calculate the threshold amount for "Preferred Return"'),
    isExpensesRestored: yup.boolean().nullable().default(false).label('Restore expenses & fees'),
    isGPPortionIncludedInLPNav: yup
      .boolean()
      .nullable()
      .default(false)
      .label('Include GP returns from contributions in LP NAV'),
    isIdleFundsIncomeIncluded: yup.boolean().nullable().default(false).label('Include idle funds'),
    isManagementFeeRestored: yup.boolean().nullable().default(false).label('Restore management fees'),
    isPremiumCatchupEnabled: yup.boolean().nullable().default(false).label('Premium Carry'),
    isProceedsPercentAdjusted: yup
      .boolean()
      .nullable()
      .default(false)
      .transform((value) => value ?? false)
      .label('Adjust distributable proceeds by the LP Commitment Percentage'),
    isProfitLossIncludedInGPCapital: yup
      .boolean()
      .nullable()
      .default(false)
      .label('Include Profit(Loss) at GP capital %'),
    isProfitLossIncludedInSLPCapital: yup
      .boolean()
      .nullable()
      .default(false)
      .label('Include Profit(Loss) at SLP capital %'),
    isSecondaryReturnEnabled: yup.boolean().nullable().default(false).label('Preferred Return - Secondary'),
    isTimeWeighted: yup.boolean().nullable().default(false).label('Time Weighted'),
    legalName: yup.string().nullable().default(null),
    lineOfCredit: usdField().nullable().default(null).label('Line of Credit'),
    lpCommitmentSplit: percentFieldHighPrecision0to100().nullable().default(80).label('LP Commitment %'),
    lpGpSplit: percentFieldHighPrecision0to100().nullable().label('Preferred Return Split').default(80),
    lpGpSplitThreshold: multiplierField().nullable().label('Preferred Return Threshold').min(0).default(3),
    lpNavCalculationType: yup
      .string()
      .oneOf(Object.values(LpNavCalculationType))
      .nullable()
      .default(LpNavCalculationType.fundWaterfall)
      .label('LP NAV Calculation Type')
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: Object.values(LpNavCalculationType).map((value) => ({
            displayName: LpNavCalculationTypeToLabel[value],
            value,
          })),
        },
      }),
    managementFeeTerminationDate: dateField()
      .nullable()
      .default(null)
      .label('Fund Close (Fees & Expenses Terminate)'),
    name: yup.string().required().default(''),
    netAssets: usdField().nullable().default(null),
    preferredReturnHurdle: percentFieldHighPrecision0to100().nullable().default(0).label('Hurdle Percentage'),
    premiumCatchupPercentage: percentFieldHighPrecision0to100()
      .nullable()
      .default(0)
      .label('Premium Carry Percentage'),
    premiumCatchupThreshold: multiplierField().nullable().label('Premium Carry Threshold').min(0).default(3),
    recyclingLimitAmount: usdField().nullable().default(null).label(''),
    recyclingLimitPercentage: percentField().nullable().default(null).label('Recycling Limit'),
    reservesAllocationAmount: usdField().nullable().default(null).label(''),
    reservesAllocationPercentage: percentField()
      .nullable()
      .default(null)
      .label('Reserves Allocation (Life of Fund)'),
    secondaryReturnHurdle: multiplierField()
      .nullable()
      .default(null)
      .label('Preferred Return - Secondary - Threshold'),
    secondaryReturnSplit: percentFieldHighPrecision0to100()
      .nullable()
      .default(null)
      .label('Preferred Return - Secondary - Split'),
    slpCommitmentSplit: percentFieldHighPrecision0to100().nullable().label('SLP Commitment Split').default(0),
    sortOrder: integerField(),
    stepDownDate: dateField().nullable().default(null),
    stockProceeds: usdField().nullable().default(null),
    strategy: yup
      .array(yup.string())
      .nullable()
      .optional()
      .default([])
      .meta({
        formatter: 'stringArray',
        renderer: RendererType.multiSelect,
        rendererMeta: {
          allowCustomAdd: true,
        },
      }),
    superReturnSplit: percentFieldHighPrecision0to100().nullable().label('Super Return Split').default(20),
    taxId: yup.string().nullable().default(null),
    totalAllocatedCapitalPercent: percentField().nullable().default(null),
    updatedAt: yup.string().nullable().default(null),
    updatedBy: yup.string().nullable().default(null),
    vintage: integerField()
      .min(2000)
      .max(2060)
      .meta({
        formatter: 'string',
        rendererMeta: {
          groupingSeparator: '',
        },
      })
      .nullable()
      .optional()
      .default(null),
  };
}

export function fundFormSchema() {
  return yup.object(fundFields());
}

export function fundProfilesFormSchema() {
  return fundFormSchema().omit(['sortOrder']);
}

export type FundDataModel = Omit<
  yup.InferType<ReturnType<typeof fundFormSchema>>,
  | 'followOnInvestingWindowCloseDate'
  | 'initialInvestingWindowCloseDate'
  | 'investingWindowInceptionDate'
  | 'managementFeeTerminationDate'
> & {
  followOnInvestingWindowCloseDate?: string | null;
  initialInvestingWindowCloseDate?: string | null;
  investingWindowInceptionDate?: string | null;
  managementFeeTerminationDate?: string | null;
};

export function createFund(overrides: Partial<FundDataModel> = {}): FundDataModel {
  return merge({}, fundFormSchema().getDefault(), overrides);
}
